import {inject, Injectable}                                                                                                                                  from '@angular/core';
import {TwColorType, TwColorUnionType}                                                                                                                       from '@djsharkk/logr-types';
import {ChartOptions, FontSpec, PluginOptionsByType, RadialLinearScaleOptions, ScaleOptions, ScaleOptionsByType, TimeScaleOptions, TimeUnit, TooltipOptions} from 'chart.js';
import Chart                                                                                                                                                 from 'chart.js/auto';
import annotationPlugin, {AnnotationOptions, BoxLabelOptions, CoreLabelOptions}                                                                              from 'chartjs-plugin-annotation';
import ChartDataLabels                                                                                                                                       from 'chartjs-plugin-datalabels';
import {LabelOptions}                                                                                                                                        from 'chartjs-plugin-datalabels/types/options';
import {DateTime}                                                                                                                                            from 'luxon';
import 'chartjs-adapter-luxon';
import * as patternomaly                                                                                                                                     from 'patternomaly';
import tinycolor                                                                                                                                             from 'tinycolor2';
import {UiThemeService}                                                                                                                                      from '../../logr-ui/utils/ui-theme.service';


Chart.register(ChartDataLabels);
Chart.register(annotationPlugin);

export type ChartTimeFormatIntf = {
	[key: string]: string
}

@Injectable({
				providedIn: 'root'
			})
export class ChartConfsService {

	svcUiTheme = inject(UiThemeService);


	chartColors = {
		tickLines  : this.svcUiTheme.getTailwindColor('neutral', 500, 0.05),
		tickFonts  : this.svcUiTheme.getTailwindColor('neutral', 600, 0.5),
		textLighter: this.svcUiTheme.getTailwindColor('neutral', 200),
		textDarker : this.svcUiTheme.getTailwindColor('neutral', 600)
	};

	chartFonts: {
		scale: FontSpec,
		body: FontSpec,
		header: FontSpec,
		datalabels: FontSpec,
	} = {
		scale     : {
			size      : 9,
			family    : 'decima_mono_roundbold',
			style     : 'normal',
			weight    : 'normal',
			lineHeight: 1
		},
		datalabels: {
			size      : 6,
			family    : 'decima_mono_roundregular',
			style     : 'normal',
			weight    : 'normal',
			lineHeight: 1
		},
		body      : {
			size      : 9,
			family    : 'decima_mono_roundregular',
			style     : 'normal',
			weight    : 'normal',
			lineHeight: 1
		},
		header    : {
			size      : 9,
			family    : 'decima_mono_roundbold',
			style     : 'normal',
			weight    : 'normal',
			lineHeight: 1
		}
	};

	confAnnotationLabelsCore: CoreLabelOptions = {
		content: null,
		color  : this.svcUiTheme.getTailwindColor('neutral', 400),
		padding: 0,
		font   : {
			...this.chartFonts.body,
			size: 9
		}
	};


	confAnnotationLabels = {
		...this.confAnnotationLabelsCore,
		rotation       : 0,
		backgroundColor: 'transparent',
		display        : true,
		borderRadius   : 0
	} as LabelOptions;


	confAnnotationLabelsBox = {
		...this.confAnnotationLabels,
		content        : '',
		padding        : 0,
		font           : {
			...this.chartFonts.scale,
			size: 9
		},
		color          : this.svcUiTheme.getTailwindColor('neutral', 400),
		textStrokeColor: this.svcUiTheme.getTailwindColor('neutral', 950),
		textStrokeWidth: 2
	} as BoxLabelOptions;

	annoOptsLine: AnnotationOptions<'line'> = {
		type       : 'line',
		borderColor: this.svcUiTheme.getTailwindColor('neutral', 500),
		borderWidth: 1
	};

	annoOptsBox: AnnotationOptions<'box'> = {
		type           : 'box',
		backgroundColor: this.svcUiTheme.getTailwindColor('neutral', 500),
		borderColor    : this.svcUiTheme.getTailwindColor('neutral', 500),
		borderWidth    : 0
	};

	confAnnotationLabel = (content: string): AnnotationOptions<'label'> => ({
		...this.confAnnotationLabelsCore,
		type: 'label',
		content
	});

	themeAnnotationBox = (
		xMin: string,
		xMax: string,
		content: string[],
		color: TwColorUnionType,
		solid?: boolean
	) => {
		return {
			...this.annoOptsBox,
			xMin,
			xMax,
			label          : {
				...this.confAnnotationLabelsBox,
				align   : 'top',
				display : true,
				rotation: -90,
				content
			},
			backgroundColor: solid
							 ? color
							 : patternomaly.draw(
					'diagonal',
					tinycolor(color).setAlpha(0).toRgbString(),
					color,
					8
				)
		};
	};

	buildScaleX(): ScaleOptionsByType<any> {
		return {
			display: true,
			offset : false,
			title  : {
				display: false
			},
			ticks  : {
				display    : true,
				align      : 'center',
				mirror     : true,
				padding    : 0,
				font       : this.chartFonts.scale,
				color      : this.chartColors.tickFonts,
				minRotation: 0,
				maxRotation: 0,
				z          : 10
			},
			border : {
				display: false
			},
			grid   : {
				display        : false,
				drawOnChartArea: true,
				tickLength     : 0,
				color          : this.chartColors.tickLines,
				drawBorder     : false,
				drawTicks      : false


			}
		} as ScaleOptions;
	}

	buildScaleY(): ScaleOptionsByType<any> {
		return {

			offset      : false,
			display     : true,
			position    : 'right',
			suggestedMin: 0,
			title       : {
				display: false
			},
			ticks       : {
				display: true,
				mirror : false,
				align  : 'right',
				padding: 0,
				font   : this.chartFonts.scale,
				color  : this.chartColors.tickFonts
			},

			border: {
				display: false
			},
			grid  : {
				display   : true,
				drawTicks : false,
				color     : this.chartColors.tickLines,
				tickLength: 0
			}
		} as ScaleOptions;
	}

	buildDefaultPlugins(): PluginOptionsByType<any> {
		let plugins = {
			title     : {
				display: false
			},
			legend    : {
				display: false,
				align  : 'start',
				labels : {
					font      : {
						...this.chartFonts.header,
						weight: 400
					},
					padding   : 0,
					boxPadding: 0,
					boxWidth  : 6,
					boxHeight : 6,
					// pointStyleWidth: 3,
					usePointStyle: true
				}

			},
			tooltip   : {
				mode             : 'nearest',
				cornerRadius     : 0,
				titleMarginBottom: 6,
				displayColors    : false,
				backgroundColor  : this.svcUiTheme.getTailwindColor('neutral', 950),
				titleFont        : {
					...this.chartFonts.header,
					size: 10
				},
				bodyFont         : {
					...this.chartFonts.body,
					size: 10
				}
			} as TooltipOptions,
			datalabels: {
				display: false,
				font   : this.chartFonts.datalabels,
				color  : this.chartColors.textLighter
				// textStrokeColor: this.svcUiTheme.getColorCode('neutral', 950),
				// textStrokeWidth: 2
			},
			annotation: {
				annotations: {}
			}
		};
		return plugins as unknown as PluginOptionsByType<any>;
	}

	defaultChartOptions(): ChartOptions {

		return {
			responsive         : true,
			maintainAspectRatio: false,
			layout             : {
				padding    : 0,
				autoPadding: false
			},
			animation          : {
				duration: 500,
				easing  : 'easeOutSine'
			},
			scales             : {
				x: this.buildScaleX(),
				y: this.buildScaleY()
			},
			plugins            : this.buildDefaultPlugins()
		};

	}

	doughnutChartOptions(): ChartOptions<'doughnut'> {
		const defaultChartOptions = this.defaultChartOptions() as ChartOptions<'doughnut'>;

		defaultChartOptions.layout = {
			...defaultChartOptions.layout,
			padding: 0
		};

		defaultChartOptions.scales = {
			...defaultChartOptions.scales,
			x: {
				...defaultChartOptions.scales?.['x'],
				display: false
			} as RadialLinearScaleOptions,
			y: {
				...defaultChartOptions.scales?.['y'],
				display: false
			} as RadialLinearScaleOptions
		};

		return defaultChartOptions;

	}

	timeChartOptions(
		unit: TimeUnit = 'day',
		formats: ChartTimeFormatIntf
	): ChartOptions<any> {
		const chartOpts = this.defaultChartOptions();

		chartOpts.scales = {
			x: {
				...chartOpts.scales?.['x'] as ScaleOptions,
				//@ts-ignore
				type : 'time',
				time : {
					unit,
					displayFormats: {
						...formats
					}
				},
				ticks: {
					...chartOpts.scales?.['x']?.ticks,
					callback: (tickValue: number, i: number) => {
						let luxonDateTime = DateTime.fromMillis(tickValue);
						if (unit === 'day') {
							return (luxonDateTime.weekday === 1 || luxonDateTime.weekday === 5) ? luxonDateTime.toFormat('ccc').toUpperCase() : luxonDateTime.toFormat(formats[unit]);
						} else {
							return luxonDateTime.toFormat(formats[unit]).toUpperCase();
						}
					}
				}
			} as TimeScaleOptions,
			y: {
				...chartOpts.scales?.['y'] as ScaleOptions
			}
		};

		chartOpts.plugins = {
			...chartOpts.plugins,
			tooltip: {
				...chartOpts.plugins?.tooltip,
				callbacks: {
					title: context => {
						return DateTime.fromMillis(context[0].parsed.x).toFormat('dd LLL').toUpperCase();
					},
					label: context => {
						const d = context;
						return (`${context.dataset.label}: ${context.formattedValue}`).toUpperCase();
					}
				}
			}
		};

		/*		chartOpts.scales.x = {
		 ...chartOpts.scales.x,
		 type: 'time',
		 time: {
		 unit,
		 displayFormats: {
		 ...formats
		 }
		 }
		 } as TimeScaleOptions;*/

		return chartOpts;
	}


	logrChartPattern = (color: TwColorType, dark: boolean = false) => {
		return patternomaly.draw(
			'diagonal',
			this.svcUiTheme.getTailwindColor(color, 950),
			this.svcUiTheme.getTailwindColor(color, dark ? 900 : 500),
			8
		)
	}
}
